import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Fade,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';

const WelcomeScreen = ({ onStartNew, onStartCompleteHome, onLoadProject, hasSavedProjects, language = 'en' }) => {
  const content = {
    en: {
      greeting: 'Welcome to',
      title: 'RumahKira',
      description: 'Plan your dream home budget in minutes',
      completeHomeTitle: 'Complete Home Estimate',
      completeHomeDesc: 'Full material & cost breakdown with detailed calculations for every phase of your build.',
      completeHomeTag: 'Recommended',
      completeHomeTime: '5-10 min',
      quickEstimateTitle: 'Quick Estimate',
      quickEstimateDesc: 'Get a ballpark budget in 3 simple steps. Perfect for early-stage planning.',
      quickEstimateTime: '1-2 min',
      loadButton: 'Recent Projects',
      footer: 'For planning purposes only',
    },
    my: {
      greeting: 'Selamat datang ke',
      title: 'RumahKira',
      description: 'Rancang bajet rumah impian anda dalam beberapa minit',
      completeHomeTitle: 'Anggaran Rumah Lengkap',
      completeHomeDesc: 'Pecahan bahan & kos penuh dengan pengiraan terperinci untuk setiap fasa pembinaan.',
      completeHomeTag: 'Disyorkan',
      completeHomeTime: '5-10 min',
      quickEstimateTitle: 'Anggaran Pantas',
      quickEstimateDesc: 'Dapatkan anggaran bajet dalam 3 langkah mudah. Sesuai untuk perancangan awal.',
      quickEstimateTime: '1-2 min',
      loadButton: 'Projek Terkini',
      footer: 'Untuk tujuan perancangan sahaja',
    }
  };

  const t = content[language] || content.en;

  // Minimal house SVG illustration
  const HouseIllustration = () => (
    <svg viewBox="0 0 120 100" style={{ width: 100, height: 84 }}>
      {/* Roof */}
      <polygon points="60,8 10,45 110,45" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      {/* Walls */}
      <rect x="20" y="45" width="80" height="45" fill="none" stroke="currentColor" strokeWidth="2.5" rx="1" />
      {/* Door */}
      <rect x="48" y="58" width="24" height="32" fill="none" stroke="currentColor" strokeWidth="2" rx="2" />
      <circle cx="67" cy="76" r="1.5" fill="currentColor" />
      {/* Left window */}
      <rect x="28" y="55" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" rx="1" />
      <line x1="35" y1="55" x2="35" y2="69" stroke="currentColor" strokeWidth="1" />
      <line x1="28" y1="62" x2="42" y2="62" stroke="currentColor" strokeWidth="1" />
      {/* Right window */}
      <rect x="78" y="55" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" rx="1" />
      <line x1="85" y1="55" x2="85" y2="69" stroke="currentColor" strokeWidth="1" />
      <line x1="78" y1="62" x2="92" y2="62" stroke="currentColor" strokeWidth="1" />
    </svg>
  );

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 6,
          textAlign: 'center',
        }}
      >
        {/* Illustration */}
        <Box sx={{ color: 'primary.main', mb: 3, opacity: 0.85 }}>
          <HouseIllustration />
        </Box>

        {/* Greeting */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.disabled',
            fontWeight: 500,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            mb: 0.5,
          }}
        >
          {t.greeting}
        </Typography>

        {/* App Name */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            mb: 1,
            letterSpacing: '-0.03em',
          }}
        >
          {t.title}
        </Typography>

        {/* Tagline */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mb: 5,
            maxWidth: 260,
            lineHeight: 1.5,
          }}
        >
          {t.description}
        </Typography>

        {/* Option Cards */}
        <Box sx={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Complete Home Estimate — Primary */}
          <Box
            onClick={onStartCompleteHome}
            sx={{
              p: 0,
              borderRadius: 3,
              cursor: 'pointer',
              overflow: 'hidden',
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #312E81 0%, #4338CA 100%)'
                  : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
              color: '#fff',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(79, 70, 229, 0.35)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    {t.completeHomeTitle}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.5, fontSize: '0.8rem', mb: 1.5 }}>
                {t.completeHomeDesc}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.03em',
                  }}
                >
                  {t.completeHomeTag}
                </Box>
                <Typography sx={{ fontSize: '0.65rem', opacity: 0.7 }}>
                  {t.completeHomeTime}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Quick Estimate — Secondary */}
          <Box
            onClick={onStartNew}
            sx={{
              px: 2.5,
              py: 2,
              borderRadius: 3,
              cursor: 'pointer',
              border: '1.5px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: 'primary.light',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(79, 70, 229, 0.04)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem' }}>
                {t.quickEstimateTitle}
              </Typography>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.8rem', mb: 1 }}>
              {t.quickEstimateDesc}
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
              {t.quickEstimateTime}
            </Typography>
          </Box>
        </Box>

        {/* Recent Projects */}
        {hasSavedProjects && (
          <Box
            onClick={onLoadProject}
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              opacity: 0.6,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 1 },
            }}
          >
            <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.85rem',
              }}
            >
              {t.loadButton}
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Typography
          variant="caption"
          sx={{
            mt: 6,
            color: 'text.disabled',
            fontSize: '0.65rem',
            letterSpacing: '0.02em',
          }}
        >
          {t.footer}
        </Typography>
      </Box>
    </Fade>
  );
};

export default WelcomeScreen;
